import React, { useRef, useState } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    useMediaQuery,
    useTheme,
    Paper,
    Grid,
    Card,
    CardContent,
    Stack,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    Group as GroupIcon,
    Comment as CommentIcon,
    Delete as DeleteIcon,
    FactCheckOutlined as FactCheckOutlinedIcon,
    WarningAmberRounded as WarningIcon
} from '@mui/icons-material';

import UnitLevelProgress from './DOKReport/UnitLevelProgress';
import BatchUnitProgressCardGenerator from './DOKReport/BatchUnitProgressCardGenerator';
import ProgressCardDeleteTab from './DOKReport/ProgressCardDeleteTab';
import SmartSubjectComment from './DOKReport/SmartSubjectComment';
import BatchSmartSubjectComment from './DOKReport/BatchSmartSubjectComment'
import DokChecklistGenerator from './DOKReport/DokChecklistGenerator';

const ManageStudentProgress = ({ setActiveTabGlobal }) => {
    const [activeTab, setActiveTab] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    // Record Assessment (tab 0) tracks unsaved edits internally.
    // Switching to another tab unmounts it outright, so this ref
    // is checked before letting that switch happen — otherwise
    // any unsaved work is discarded with no warning at all.
    const recordAssessmentRef = useRef(null);

    const [unsavedTabWarning, setUnsavedTabWarning] = useState({
        open: false,
        targetTab: null,
        studentNames: []
    });

    const requestTabChange = (targetTab) => {
        if (targetTab === activeTab) {
            return;
        }

        if (
            activeTab === 0 &&
            recordAssessmentRef.current?.hasUnsavedChanges?.()
        ) {
            setUnsavedTabWarning({
                open: true,
                targetTab,
                studentNames:
                    recordAssessmentRef.current.getUnsavedStudentNames?.() || []
            });
            return;
        }

        setActiveTab(targetTab);
    };

    const handleCancelTabWarning = () => {
        setUnsavedTabWarning({ open: false, targetTab: null, studentNames: [] });
    };

    const handleLeaveTabAnyway = () => {
        const { targetTab } = unsavedTabWarning;
        setUnsavedTabWarning({ open: false, targetTab: null, studentNames: [] });
        if (targetTab !== null) {
            setActiveTab(targetTab);
        }
    };

    const tabData = [
        {
            index: 0,
            label: "Record Assessment",
            shortLabel: "Batch Report",
            icon: <GroupIcon />,
            description: "Record the skills students have demonstrated",
            isPrimary: true,
            component: <BatchUnitProgressCardGenerator ref={recordAssessmentRef} />
        },
        {
            index: 1,
            label: "Differentiate Skills",
            shortLabel: "Single Report",
            icon: <AssessmentIcon />,
            description: "Adjust skills for individual students",
            component: <UnitLevelProgress />
        },
        {
            index: 2,
            label: "Single Student Comment",
            shortLabel: "Smart Comment",
            icon: <CommentIcon />,
            description: "Create a final comment for one student",
            component: <SmartSubjectComment setActiveTabGlobal={setActiveTabGlobal} />
        },
        {
            index: 3,
            label: "Class Comments",
            shortLabel: "Smart Comment",
            icon: <CommentIcon />,
            description: "Create final comments for multiple students",
            component: <BatchSmartSubjectComment setActiveTabGlobal={setActiveTabGlobal} />
        },
        {
            index: 4,
            label: "DOK Checklist",
            shortLabel: "DOK Checklist",
            icon: <FactCheckOutlinedIcon />,
            description: "Print a blank skills checklist for a unit",
            component: <DokChecklistGenerator />
        }

    ];

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,

                minHeight: '100vh',
                p: { xs: 2, sm: 3, md: 4 },
                maxWidth: "100%",
                mx: "auto"
            }}
        >
            {/* Header */}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}><AssessmentIcon /></Avatar>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">Manage Assessments</Typography>
                    <Typography variant="body2" color="text.secondary">Record student progress by updating DOK levels, assigning proficiency, and generating AI-powered comments.</Typography>
                    <Typography variant="body2" color="text.secondary"><br></br></Typography>
                </Box>
            </Box>

            {/* Navigation */}
            {isMobile ? (
                <Stack spacing={2}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color: theme.palette.primary.dark,
                            textAlign: "center",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            mb: 1
                        }}
                    >
                        Select Section
                    </Typography>
                    <Select
                        fullWidth
                        value={activeTab}
                        onChange={(e) => requestTabChange(e.target.value)}
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            borderRadius: 2,
                            mb: 4,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 600,
                            fontSize: '1rem',
                            '& .MuiSelect-select': { py: 2, px: 2.5 },
                            '& .MuiMenuItem-root': { fontSize: '1rem' }
                        }}
                    >
                        {tabData.map((tab) => (
                            <MenuItem key={tab.index} value={tab.index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ fontSize: '1.25rem' }}>{tab.icon}</Box>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>{tab.label}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
            ) : (
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 4, md: 5 }, maxWidth: '1200px', mx: 'auto' }}>
                    {tabData.map((tab) => {
                        const isActive = activeTab === tab.index;

                        return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={tab.index}>
                            <Card
                                elevation={isActive ? 4 : (tab.isPrimary ? 2 : 1)}
                                sx={{
                                    position: 'relative',
                                    height: '240px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    border: isActive
                                        ? `2px solid ${theme.palette.primary.main}`
                                        : tab.isPrimary
                                            ? `2px solid ${theme.palette.primary.light}`
                                            : '1px solid',
                                    borderColor: isActive
                                        ? 'primary.main'
                                        : tab.isPrimary
                                            ? 'primary.light'
                                            : 'divider',
                                    backgroundColor: isActive ? 'primary.main' : 'background.paper',
                                    color: isActive ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    }
                                }}
                                onClick={() => requestTabChange(tab.index)}
                            >
                                <CardContent sx={{
                                    textAlign: 'center',
                                    p: { xs: 1.5, sm: 2, md: 2.5 },
                                    '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 1.5 } }}>
                                        <Box sx={{
                                            p: { xs: 1, sm: 1.5 },
                                            borderRadius: '50%',
                                            backgroundColor: activeTab === tab.index ? 'primary.contrastText' : 'grey.100',
                                            color: activeTab === tab.index ? 'primary.main' : 'grey.600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                                        }}>
                                            {tab.icon}
                                        </Box>
                                    </Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                            color: 'inherit',
                                            mb: { xs: 0.5, sm: 0.75 },
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {tab.label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                                            color: 'inherit',
                                            opacity: 0.85,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {tab.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Content */}
            <Box sx={{ '& > *': { width: '100%' } }}>
                {tabData[activeTab].component}
            </Box>

            {/* Unsaved Changes Warning — leaving Record Assessment
                for another tab unmounts it and discards any
                unsaved edits, so this interrupts and names them
                rather than letting that happen silently. */}
            <Dialog
                open={unsavedTabWarning.open}
                onClose={handleCancelTabWarning}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: '#9A6A00' }}>
                    <WarningIcon sx={{ color: '#EFCE86' }} />
                    Unsaved Changes
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {`Leaving Record Assessment will discard unsaved changes for ${unsavedTabWarning.studentNames.length} student${unsavedTabWarning.studentNames.length === 1 ? '' : 's'}:`}
                    </Typography>

                    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                        {unsavedTabWarning.studentNames.map((name) => (
                            <Typography key={name} variant="body2" fontWeight={600}>
                                • {name}
                            </Typography>
                        ))}
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                        Go back to Record Assessment and save their changes first, or leave and lose them.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        onClick={handleCancelTabWarning}
                        sx={{ textTransform: 'none', fontWeight: 700, color: '#586735' }}
                    >
                        Go Back
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleLeaveTabAnyway}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            backgroundColor: '#9A6A00',
                            '&:hover': { backgroundColor: '#7D5600' }
                        }}
                    >
                        Leave Anyway
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageStudentProgress;
